/* QuizEditor — author/edit a topic's quiz inline in the DocEditorDrawer.
 * Super-admin only (training:write). Loads on demand when the section is
 * expanded so the drawer stays snappy for non-quiz edits.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  HelpCircle, Plus, Trash2, Save, Loader2, AlertTriangle, CheckCircle2,
  ChevronRight, ChevronDown, GraduationCap, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple choice (1 correct)' },
  { value: 'multi_select', label: 'Multi-select (≥1 correct)' },
  { value: 'true_false', label: 'True / False' },
];

const blankQuestion = (type = 'multiple_choice') => ({
  type,
  prompt: '',
  options: type === 'true_false'
    ? [{ text: 'True', is_correct: false }, { text: 'False', is_correct: false }]
    : [{ text: '', is_correct: false }, { text: '', is_correct: false }],
  explanation: '',
});

const QuizEditor = ({ docId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiNote, setAiNote] = useState('');
  const [quiz, setQuiz] = useState({
    questions: [], passing_score: 80, shuffle_questions: true, exists: false,
  });

  const load = useCallback(async () => {
    if (!docId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/docs/${docId}/quiz`,
        { withCredentials: true });
      setQuiz({
        questions: r.data.questions || [],
        passing_score: r.data.passing_score ?? 80,
        shuffle_questions: r.data.shuffle_questions ?? true,
        exists: !!r.data.exists,
      });
      setLoaded(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load quiz.');
    }
    setLoading(false);
  }, [docId]);

  useEffect(() => { if (expanded && !loaded) load(); }, [expanded, loaded, load]);

  const updateQuestion = (i, patch) => {
    const next = [...quiz.questions];
    next[i] = { ...next[i], ...patch };
    setQuiz({ ...quiz, questions: next });
  };
  const updateOption = (qi, oi, patch) => {
    const next = [...quiz.questions];
    const opts = [...(next[qi].options || [])];
    opts[oi] = { ...opts[oi], ...patch };
    // For multiple_choice and true_false, only one option may be correct.
    if (patch.is_correct === true && next[qi].type !== 'multi_select') {
      opts.forEach((o, j) => { if (j !== oi) o.is_correct = false; });
    }
    next[qi] = { ...next[qi], options: opts };
    setQuiz({ ...quiz, questions: next });
  };
  const addOption = (qi) => updateQuestion(qi, {
    options: [...(quiz.questions[qi].options || []), { text: '', is_correct: false }],
  });
  const removeOption = (qi, oi) => updateQuestion(qi, {
    options: (quiz.questions[qi].options || []).filter((_, j) => j !== oi),
  });
  const changeType = (qi, type) => {
    if (type === 'true_false') {
      updateQuestion(qi, {
        type,
        options: [{ text: 'True', is_correct: false }, { text: 'False', is_correct: false }],
      });
    } else {
      updateQuestion(qi, { type });
    }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = {
        passing_score: Number(quiz.passing_score) || 80,
        shuffle_questions: !!quiz.shuffle_questions,
        questions: quiz.questions.map((q) => ({
          type: q.type, prompt: (q.prompt || '').trim(),
          explanation: (q.explanation || '').trim() || null,
          options: (q.options || [])
            .filter((o) => (o.text || '').trim())
            .map((o) => ({ text: o.text.trim(), is_correct: !!o.is_correct })),
        })).filter((q) => q.prompt && q.options.length >= 2),
      };
      await axios.put(`${BACKEND_URL}/api/admin/docs/${docId}/quiz`, payload,
        { withCredentials: true });
      setSavedAt(new Date());
      setQuiz((prev) => ({ ...prev, exists: true }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  const aiDraftQuiz = async () => {
    if (quiz.questions.length > 0 &&
        !window.confirm('This will append 3 AI-drafted questions to the existing list. Continue?')) {
      return;
    }
    setAiDrafting(true); setError(''); setAiNote('');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/docs/${docId}/quiz/ai-draft`, {},
        { withCredentials: true });
      const drafted = r.data?.questions || [];
      if (!drafted.length) throw new Error('AI returned no questions.');
      setQuiz((q) => ({ ...q, questions: [...q.questions, ...drafted] }));
      setAiNote(`AI drafted ${drafted.length} question(s). Review and click Save quiz when ready.`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'AI quiz draft failed.');
    }
    setAiDrafting(false);
  };

  const removeQuiz = async () => {
    if (!window.confirm('Delete the entire quiz for this topic?')) return;
    setSaving(true); setError('');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/docs/${docId}/quiz`,
        { withCredentials: true });
      setQuiz({ questions: [], passing_score: 80, shuffle_questions: true, exists: false });
      setSavedAt(null);
    } catch (e) { setError(e.response?.data?.detail || 'Delete failed.'); }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg" data-testid="quiz-editor">
      <button onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center justify-between p-3 hover:bg-[#FAFBFC] transition-colors"
        data-testid="quiz-editor-toggle">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#4B5563]">
          <GraduationCap className="w-3.5 h-3.5 text-[#BA1826]" />
          Quiz / Knowledge check
          {quiz.exists && (
            <span className="text-[10px] text-emerald-700 normal-case font-normal">
              ({quiz.questions.length} question{quiz.questions.length === 1 ? '' : 's'} · pass ≥ {quiz.passing_score}%)
            </span>
          )}
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />}
      </button>

      {expanded && (
        <div className="border-t border-[#E5E7EB] p-3 space-y-3">
          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded inline-flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
                </div>
              )}

              {/* Settings row */}
              <div className="flex flex-wrap items-end gap-3 bg-[#FAFBFC] border border-[#E5E7EB] rounded p-2">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF] mb-1">
                    Passing score (%)
                  </label>
                  <input type="number" min="50" max="100" value={quiz.passing_score}
                    onChange={(e) => setQuiz({ ...quiz, passing_score: e.target.value })}
                    className="w-20 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono"
                    data-testid="quiz-passing-score" />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-[#4B5563]">
                  <input type="checkbox" checked={!!quiz.shuffle_questions}
                    onChange={(e) => setQuiz({ ...quiz, shuffle_questions: e.target.checked })}
                    data-testid="quiz-shuffle" />
                  Shuffle questions
                </label>
                <p className="text-[10px] text-[#9CA3AF] flex-1 min-w-[200px]">
                  Passing this quiz automatically marks the topic complete for the learner. Unlimited retakes.
                </p>
              </div>

              {/* Questions */}
              {quiz.questions.length === 0 && (
                <p className="text-[11px] italic text-[#9CA3AF] py-2">
                  No questions yet. Add a question to enable the quiz on this topic.
                </p>
              )}
              {quiz.questions.map((q, qi) => (
                <div key={qi} className="border border-[#E5E7EB] rounded p-2.5 space-y-2"
                  data-testid={`quiz-q-${qi}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-[10px] font-bold text-[#9CA3AF] mt-1.5">Q{qi + 1}</span>
                    <textarea value={q.prompt || ''}
                      onChange={(e) => updateQuestion(qi, { prompt: e.target.value })}
                      placeholder="Question text"
                      rows={2}
                      className="flex-1 px-2 py-1.5 border border-[#E5E7EB] rounded text-xs"
                      data-testid={`quiz-q-prompt-${qi}`} />
                    <select value={q.type}
                      onChange={(e) => changeType(qi, e.target.value)}
                      className="px-2 py-1.5 border border-[#E5E7EB] rounded text-xs bg-white">
                      {QUESTION_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <button onClick={() => setQuiz({ ...quiz,
                        questions: quiz.questions.filter((_, j) => j !== qi) })}
                      className="text-[#BA1826] mt-1.5"
                      data-testid={`quiz-q-remove-${qi}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>

                  {(q.options || []).map((o, oi) => (
                    <div key={oi} className="flex items-center gap-1.5 ml-6">
                      <input type={q.type === 'multi_select' ? 'checkbox' : 'radio'}
                        name={`q-${qi}-correct`}
                        checked={!!o.is_correct}
                        onChange={(e) => updateOption(qi, oi, { is_correct: e.target.checked })}
                        data-testid={`quiz-q-${qi}-correct-${oi}`} />
                      <input value={o.text || ''}
                        onChange={(e) => updateOption(qi, oi, { text: e.target.value })}
                        placeholder={`Option ${oi + 1}`}
                        disabled={q.type === 'true_false'}
                        className="flex-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs disabled:bg-[#F8F9FA]"
                        data-testid={`quiz-q-${qi}-opt-${oi}`} />
                      {q.type !== 'true_false' && q.options.length > 2 && (
                        <button onClick={() => removeOption(qi, oi)}
                          className="text-[#9CA3AF] hover:text-[#BA1826]">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {q.type !== 'true_false' && q.options.length < 8 && (
                    <button onClick={() => addOption(qi)}
                      className="text-[10px] text-[#BA1826] ml-6 inline-flex items-center gap-1"
                      data-testid={`quiz-q-${qi}-add-opt`}>
                      <Plus className="w-3 h-3" /> Add option
                    </button>
                  )}
                  <input value={q.explanation || ''}
                    onChange={(e) => updateQuestion(qi, { explanation: e.target.value })}
                    placeholder="Explanation shown after answering (optional, but recommended for learning)"
                    className="ml-6 w-[calc(100%-1.5rem)] px-2 py-1 border border-[#E5E7EB] rounded text-xs"
                    data-testid={`quiz-q-${qi}-explanation`} />
                </div>
              ))}

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button onClick={() => setQuiz({ ...quiz,
                    questions: [...quiz.questions, blankQuestion('multiple_choice')] })}
                  className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1"
                  data-testid="quiz-add-question">
                  <Plus className="w-3 h-3" /> Add question
                </button>
                <button onClick={aiDraftQuiz} disabled={aiDrafting}
                  className="text-xs px-2.5 py-1.5 border border-[#BA1826]/40 bg-[#FFF8F8] text-[#BA1826] rounded inline-flex items-center gap-1.5 hover:bg-[#BA1826] hover:text-white transition-colors disabled:opacity-60"
                  data-testid="quiz-ai-draft">
                  {aiDrafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  AI-draft a 3-question quiz
                </button>
                <button onClick={save} disabled={saving || quiz.questions.length === 0}
                  className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="quiz-save">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save quiz
                </button>
                {quiz.exists && (
                  <button onClick={removeQuiz} disabled={saving}
                    className="text-xs px-2.5 py-1.5 border border-red-200 text-red-700 rounded inline-flex items-center gap-1"
                    data-testid="quiz-delete">
                    <Trash2 className="w-3 h-3" /> Delete quiz
                  </button>
                )}
                {savedAt && (
                  <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Saved {savedAt.toLocaleTimeString()}
                  </span>
                )}
                {aiNote && (
                  <span className="text-[10px] text-[#BA1826] inline-flex items-center gap-1"
                    data-testid="quiz-ai-note">
                    <Sparkles className="w-3 h-3" /> {aiNote}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-[#9CA3AF] inline-flex items-center gap-1">
                <HelpCircle className="w-3 h-3" />
                Mark the correct option(s) by clicking the radio / checkbox. Learners see questions and options in random order if shuffle is enabled.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizEditor;
